import {
	ChangeDetectorRef,
	Component,
	OnInit,
	ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
	StripeCardCvcElementOptions,
	StripeCardExpiryElementOptions,
	StripeCardNumberElementOptions,
} from '@stripe/stripe-js';
import {
	ZkELowerCasedCountryCodes,
	ZkICheckoutSummaryItem,
	ZkIPaymentBoletoFormUpdate,
	ZkIPaymentBoletoUserDataInvalidMessage,
	ZkIPaymentMethodBoleto,
	ZkIPaymentMethodCard,
	ZkIPaymentSelectedCard,
	ZkIStripeWithBoleto,
	ZkNotificationService,
	ZkTPaymentMethodName,
} from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import {
	ICreateCartPayload,
	IProfessionalCart,
	IUpdateCartPayload,
} from 'app/interfaces/checkout.interface';
import { ApiError, ApiErrorResponse } from 'app/interfaces/error.interface';
import { IRegion } from 'app/interfaces/location.interface';
import { IProfessionalProfile } from 'app/interfaces/professionalprofile.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { CheckoutService } from 'app/services/checkout.service';
import { ErrorService } from 'app/services/error.service';
import { GlobalService } from 'app/services/global.service';
import { LocationService } from 'app/services/location.service';
import { StripeService } from 'app/services/stripe.service';
import { UserService } from 'app/services/user.service';
import { first } from 'rxjs/operators';

@Component({
	selector: 'app-checkout',
	templateUrl: './checkout.component.html',
	styleUrls: ['./checkout.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class CheckoutComponent implements OnInit {
	isMobile = false;
	userData: IProfessionalProfile;

	cart: IProfessionalCart;
	stripe: ZkIStripeWithBoleto;

	visibleProducts: boolean;
	country: ZkELowerCasedCountryCodes;
	regions: IRegion[] = [];
	loadingCart = false;
	loadingPayment = false;
	rightNow: boolean;
	selectedPaymentType: ZkTPaymentMethodName;

	profile: IProfessionalProfile;

	stripeCardCvcOptions: StripeCardCvcElementOptions = {
		placeholder: this.translateService.instant('CHECKOUT.CARD_CVC'),
	};
	stripeCardExpiryOptions: StripeCardExpiryElementOptions = {
		placeholder: this.translateService.instant('CHECKOUT.CARD_EXPIRY'),
	};
	stripeCardNumberOptions: StripeCardNumberElementOptions = {
		placeholder: this.translateService.instant('CHECKOUT.CARD_NUMBER'),
		showIcon: true,
	};

	boletoIsValid: ZkIPaymentBoletoFormUpdate['isValid'];
	boletoInvalidMessages: ZkIPaymentBoletoUserDataInvalidMessage;

	constructor(
		private actionTrackerService: ActionTrackerService,
		private changeDetectorRef: ChangeDetectorRef,
		private checkoutService: CheckoutService,
		private errorService: ErrorService,
		private globalService: GlobalService,
		private location: LocationService,
		private notifications: ZkNotificationService,
		private route: ActivatedRoute,
		private router: Router,
		private stripeService: StripeService,
		private translateService: TranslateService,
		private userService: UserService
	) {
		this.location.getCountryRegions(1).subscribe((r) => (this.regions = r));
		this.translateBoletoInvalidMessages();
	}

	ngAfterViewChecked() {
		// your code to update the model
		this.changeDetectorRef.detectChanges();
	}

	async ngOnInit() {
		this.onResize();
		this.getUserData();

		this.route.queryParams.pipe(first()).subscribe((params) => {
			// const  = this.route.snapshot.params['id'];
			const { rightNow, source = 'unknown', cartId, pid: planId } = params;

			if (rightNow) {
				this.rightNow = rightNow;
			}

			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Payment,
				SUBSCOPE_TYPE.Checkout,
				ITEM_TYPE.Cart,
				INTERACTION_TYPE.Open,
				{
					sourceComponent: source,
					...(!!cartId && { cartId }),
					...(!!planId && { subscriptionId: planId }),
				}
			);

			if (!!cartId) {
				return this.getCart(cartId);
			}

			if (!!planId) {
				return this.createCart({ planId, source: 'web' });
			}

			this.router.navigateByUrl('/account/premium');
		});

		this.stripe = await this.stripeService.load();
	}

	checkPaymentMethod() {
		if (this.cart) {
			if (this.cart.summary.totalClientCharge.value <= 0) {
				return true;
			}

			const selectedPaymentMethod = this.cart.paymentMethods.find(
				(item) => item.selected
			);

			this.selectedPaymentType =
				(selectedPaymentMethod && selectedPaymentMethod.type) || null;

			if (!!selectedPaymentMethod) {
				switch (selectedPaymentMethod.type) {
					case 'card':
						const hasSelectedCard =
							(selectedPaymentMethod.newCard &&
								!!selectedPaymentMethod.newCard.element) ||
							selectedPaymentMethod.cards.some((card) => card.selected);

						return hasSelectedCard;
					case 'boleto':
						return this.boletoIsValid;
				}
			}

			return !!selectedPaymentMethod;
		}
		return false;
	}

	disablePaymentMethods() {
		return this.cart && this.cart.summary.totalClientCharge.value <= 0;
	}

	getCart(cartId: IProfessionalCart['id']) {
		this.checkoutService.getCart(cartId).subscribe({
			next: (res) => {
				this.cart = { ...res };
				this.setBoletoUserData();
			},
			error: (err: ApiErrorResponse) => {
				this.setErrorMessage((<ApiError>err.error).messageI18n, { cartId });
				this.router.navigateByUrl('/account/premium');
			},
		});
	}

	setVisibleProducts() {
		this.visibleProducts = false;
		if (!this.cart.products || !this.cart.products.visible) {
			this.visibleProducts = false;
		} else if (this.cart.products && this.cart.products.data) {
			this.visibleProducts =
				this.cart.products.data.length > 0 &&
				this.cart.products.data.some((p) => p.visible);
		}
	}

	createCart({ planId }: ICreateCartPayload) {
		this.checkoutService.createCart({ planId, source: 'web' }).subscribe({
			next: (res) => {
				this.cart = { ...res };
				this.setBoletoUserData();

				if (!!planId) {
					this.actionTrackerService.eventTracker(
						SCOPE_TYPE.Payment,
						SUBSCOPE_TYPE.Checkout,
						ITEM_TYPE.Cart,
						INTERACTION_TYPE.AddedPlan,
						{
							planId,
						}
					);
				}
			},
			error: (err: ApiErrorResponse) => {
				this.setErrorMessage((<ApiError>err.error).messageI18n, {
					planId,
				});
				this.router.navigateByUrl('/account/premium');
			},
		});
	}

	updateCart({ key, value }: IUpdateCartPayload<keyof IProfessionalCart>) {
		const newCart = {
			...this.cart,
			[key]: value,
		};

		if (key === 'paymentMethods') {
			const cardMethod = newCart.paymentMethods.find(
				(method) => method.type === 'card'
			) as ZkIPaymentMethodCard;
			if (
				cardMethod &&
				cardMethod.visible &&
				cardMethod.selected &&
				!!cardMethod.newCard
			) {
				// update cart locally with the new card element
				this.cart = newCart;
				return;
			}

			const boleto = newCart.paymentMethods.find(
				(method) => method.type === 'boleto'
			) as ZkIPaymentMethodBoleto;
			if (boleto && boleto.selected) {
				this.boletoIsValid =
					(boleto.userdata as ZkIPaymentBoletoFormUpdate).isValid === true;
				delete (boleto.userdata as ZkIPaymentBoletoFormUpdate).isValid;
				this.cart = newCart;
			}
		}

		this.loadingCart = true;
		this.checkoutService.updateCart(newCart).subscribe({
			next: (res) => {
				this.cart = { ...res };
				this.loadingCart = false;

				if (
					this.cart &&
					this.cart.summary &&
					this.cart.summary.totalClientCharge &&
					this.cart.summary.totalClientCharge.value < 5
				) {
					this.cart.paymentMethods.find((m) => m.type === 'boleto').visible =
						false;
				}
			},
			error: (err: ApiErrorResponse) => {
				this.setErrorMessage((<ApiError>err.error).messageI18n, {
					[key]: value,
				});
			},
		});
	}

	async finalizeCart() {
		this.loadingPayment = true;

		const selectedPaymentMethod = this.cart.paymentMethods.find(
			(item) => item.selected
		);

		if (selectedPaymentMethod && selectedPaymentMethod.type === 'card') {
			const selectedCard = selectedPaymentMethod.cards.find(
				(c) => c.selected && c.id
			);

			if (!selectedCard && selectedPaymentMethod.newCard) {
				const { element, name, save } = selectedPaymentMethod.newCard;
				try {
					const { result, card } = await this.stripeService.createToken({
						element,
						name,
						save,
					});

					// Inform the user if there was an error.
					if (result.error) {
						this.setErrorMessage(result.error.message, {
							planId: !!this.cart.items.plan && this.cart.items.plan.id,
							paymentMethod: selectedPaymentMethod.type,
						});

						this.loadingPayment = false;

						return false;
					} else {
						// card is created correctly in Stripe
						selectedPaymentMethod.cards.push(card);
						selectedPaymentMethod.newCard = undefined;

						return this.checkoutService.updateCart(this.cart).subscribe({
							next: (res) => {
								this.cart = { ...res };

								this.finalizeCart();
							},
							error: (err: ApiErrorResponse) => {
								this.setErrorMessage((<ApiError>err.error).messageI18n, {
									paymentMethods: this.cart.paymentMethods,
								});
								this.loadingPayment = false;
							},
						});
					}
				} catch (error) {
					this.setErrorMessage(error.message, {
						planId: !!this.cart.items.plan && this.cart.items.plan.id,
						paymentMethod: selectedPaymentMethod.type,
					});

					this.loadingPayment = false;

					return false;
				}
			}

			if (selectedCard) {
				this.checkoutService.finalizeCart(this.cart).subscribe({
					next: (res) => {
						this.cart = this.selectPaymentMethod(
							res,
							selectedPaymentMethod.type
						);

						if (res.finalized) {
							this.paymentSuccess();
							this.loadingPayment = false;
						} else {
							// there was an error lets reprocess
							this.cart.summary.isRetry = true;
							this.confirmPaymentOnStripe(selectedCard);
						}
					},
					error: (err: ApiErrorResponse) => {
						this.setErrorMessage((<ApiError>err.error).messageI18n, {
							planId: !!this.cart.items.plan && this.cart.items.plan.id,
							paymentMethod: selectedPaymentMethod.type,
						});
					},
				});
			}
		} else {
			this.checkoutService.finalizeCart(this.cart).subscribe({
				next: (res) => {
					this.cart = this.selectPaymentMethod(res, selectedPaymentMethod.type);

					if (res.finalized) {
						this.paymentSuccess();
					}

					this.loadingPayment = false;
				},
				error: (err: ApiErrorResponse) => {
					this.setErrorMessage((<ApiError>err.error).messageI18n, {
						planId: !!this.cart.items.plan && this.cart.items.plan.id,
						paymentMethod: selectedPaymentMethod.type,
					});
				},
			});
		}
	}

	confirmPaymentOnStripe(card: ZkIPaymentSelectedCard) {
		try {
			// we first check the payment intent to check its status
			this.stripe
				.retrievePaymentIntent(this.cart.summary.clientSecret)
				.then(async (pi) => {
					if (pi.error) {
						const msg = this.errorService.getMessage(pi.error, 'UNKNOWN_ERROR');
						this.setErrorMessage(msg, { cardId: card.id });
						return false;
					}

					if (pi.paymentIntent) {
						if (pi.paymentIntent.status !== 'succeeded') {
							const result = await this.stripe.confirmCardPayment(
								this.cart.summary.clientSecret,
								{
									payment_method: card.id,
									setup_future_usage: 'off_session',
								}
							);

							if (result.error) {
								const msg = this.errorService.getMessage(
									result.error,
									'UNKNOWN_ERROR'
								);
								this.setErrorMessage(msg, { cardId: card.id });
								return false;
							}
							pi = result;
						}

						// The payment has succeeded
						// Display a success message
						if (pi.paymentIntent.status === 'succeeded') {
							this.checkoutService.finalizeCart(this.cart).subscribe({
								next: (res) => {
									// all went good just move to summary page
									this.cart = { ...res };

									this.paymentSuccess();

									this.loadingPayment = false;
								},
								error: (err: ApiErrorResponse) => {
									this.setErrorMessage((<ApiError>err.error).messageI18n, {
										planId: !!this.cart.items.plan && this.cart.items.plan.id,
										cardId: card.id,
									});
								},
							});
						}
					}
				})
				.catch((err) => {
					const msg = this.errorService.getMessage(err.code, 'UNKNOWN_ERROR');
					this.setErrorMessage(msg);
				});
		} catch (err) {
			const msg = this.errorService.getMessage(err.code, 'UNKNOWN_ERROR');
			this.setErrorMessage(msg);
			return false;
		}
	}

	setErrorMessage(message: string, payload: any = {}) {
		if (!message) {
			message = this.translateService.instant('ERRORS.NOTSPECIFIED');
		}

		// bypass notification when boleto has no invalid field
		if (this.boletoIsValid || message?.length) {
			this.notifications.showError(message);
		}

		this.loadingPayment = false;
		this.loadingCart = false;

		if (message !== undefined) {
			// log error in firebase
			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Payment,
				SUBSCOPE_TYPE.Checkout,
				ITEM_TYPE.Cart,
				INTERACTION_TYPE.Error,
				{
					error: message,
					...payload,
				}
			);
		}
	}

	paymentSuccess() {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Payment,
			SUBSCOPE_TYPE.Checkout,
			ITEM_TYPE.Cart,
			INTERACTION_TYPE.Finished,
			{
				planId: this.cart.items.plan.id,
			}
		);

		this.router.navigate(['/checkout/success/'], {
			state: { cart: this.cart },
		});
	}

	handleCartItems() {
		const items: ZkICheckoutSummaryItem[] = [];

		const { plan } = this.cart.items;

		if (
			plan ||
			(this.cart.suggestedContent &&
				this.cart.suggestedContent.suggestion.isSelected)
		) {
			const summaryPlan = plan || this.cart.suggestedContent.plan;
			items.push({
				type: 'plan',
				label: `${summaryPlan.name}`,
				value: summaryPlan.price,
			});
		}

		if (this.cart.summary.discounts.length) {
			this.cart.summary.discounts.forEach(({ label, value }) =>
				items.push({
					type: 'discount',
					label,
					value,
				})
			);
		}

		return items;
	}

	selectPaymentMethod(cart: IProfessionalCart, method: ZkTPaymentMethodName) {
		cart.paymentMethods.forEach((m) => (m.selected = m.type === method));
		return cart;
	}

	onResize() {
		this.isMobile = this.globalService.isMobileDevice();
	}

	getUserData(sub?) {
		this.userService.getProfileData().then((data) => {
			this.userData = data;
			if (sub) {
				sub(data);
			}
		});
	}

	setBoletoUserData() {
		const boleto = this.cart.paymentMethods.find(
			(method) => method.type === 'boleto'
		) as ZkIPaymentMethodBoleto;

		this.getUserData(() => {
			if (boleto) {
				boleto.userdata.address = this.userData.address;
				boleto.userdata.email = this.userData.email;
				boleto.userdata.name = this.userData.displayName;
				boleto.userdata.tax_id =
					this.userData.businessFiscalId || this.userData.fiscalId;
			}
		});
	}

	translateBoletoInvalidMessages() {
		this.translateService
			.get([
				'CHECKOUT.BOLETO_FORM.INVALID.ADDRESS',
				'CHECKOUT.BOLETO_FORM.INVALID.CITY',
				'CHECKOUT.BOLETO_FORM.INVALID.FISCAL_ID',
				'CHECKOUT.BOLETO_FORM.INVALID.POSTAL_CODE',
				'CHECKOUT.BOLETO_FORM.INVALID.STATE',
			])
			.subscribe((messages) => {
				this.boletoInvalidMessages = {
					address: messages['CHECKOUT.BOLETO_FORM.INVALID.ADDRESS'],
					city: messages['CHECKOUT.BOLETO_FORM.INVALID.CITY'],
					tax_id: messages['CHECKOUT.BOLETO_FORM.INVALID.FISCAL_ID'],
					postal_code: messages['CHECKOUT.BOLETO_FORM.INVALID.POSTAL_CODE'],
					state: messages['CHECKOUT.BOLETO_FORM.INVALID.STATE'],
				};
			});
	}
}
